<template>
    <div 
        class="text-expandable-parent"
        :style="{
            maxHeight: parentHeight ? parentHeight + 'px' : null,
            transition: 'max-height ' + transitionTimeString,
            }"
    >
        <div class="text-expandable-content" ref="contentElement">
            <span 
                :class="expanded ? 'text-show-breaks' : ''"
            >{{ displayContent }}</span>
            <b-link 
                v-if="hasMore"
                class="text-uppercase font-size-70 font-weight-bold ml-2"
                @click="toggleExpand"
            >{{ expanded ? toggleTextOpen : toggleTextClosed }}</b-link>
        </div>
    </div>
</template>

<script>
import { nanoid } from 'nanoid';

export default {
    name: 'TextExpandable',
    props: {
        content: {
            type: String,
            default: '',
        },
        toggleTextClosed: {
            type: String,
            default: 'More',
        },
        toggleTextOpen: {
            type: String,
            default: 'Less',
        },
        /* initialHeight: { // initial height in pixels
            type: Number,
            default: 50,
        }, */
        wordCount: {  // cutoff after this many words and show more...
            type: Number,  // its a number, so set like :word-count="20"
            default: 10,
        },
        transitionTime: {  // transition time in milliseconds
            type: Number,  // its a number, so set like :transition-time="500"
            default: 500,
        }
    },
    data: function() {
        return {
            uuid: nanoid(),
            expanded: false,
            truncatedHeight: null,  // set this on initial mount to the height of truncated content, store it, use it as height value in future un-expanding
            displayContent: '',  // update this to full or truncated content
            parentHeight: 0, // set this in mounted hook, update when we toggle
        }
    },
    computed: {
        hasMore: function() {  // return Boolean - true if the content is longer than our wordCount cutoff
            let totalWords = this.content.split(" ").length;
            return this.wordCount < totalWords;
        },
        /* truncated: function() {
            let truncated = this.content.split(" ").splice(0, this.wordCount).join(" ");
            return truncated + (this.hasMore ? '... ' : '');
        }, */
        transitionTimeString: function() {
            // convert transitionTime in ms to string to use in CSS
            let msTime = this.transitionTime || 500;
            return (msTime / 1000).toFixed(1).toString() + 's';
        }
    },
    mounted: function() {
        // set truncated content, get the height of it, store it and set parentHeight to it
        this.displayContent = this.getTruncatedContent();
        this.$nextTick(() => {
            let truncatedHeight = this.getContentHeight();
            this.parentHeight = truncatedHeight;
            this.truncatedHeight = truncatedHeight;  // use this now and in future expand/contract
        });
    },
    methods: {
        getContentHeight: function() {
            let contentElement = this.$refs.contentElement;
            return contentElement ? contentElement.offsetHeight : 0;  // need this in case element not present in DOM yet
        },
        getTruncatedContent: function() {
            let truncated = this.content.split(" ").splice(0, this.wordCount).join(" ");
            return truncated + (this.hasMore ? '... ' : '');
        },
        toggleExpand: function() {
            if (this.expanded) {
                // when un-expanding, set height first to start collapse, then update content AFTER TIMEOUT is done, then re-capture truncatedHeight in case DOM changed
                this.parentHeight = this.truncatedHeight;  // starts the collapse
                let delayTime = this.transitionTime;  // add buffer to be safe?  probably no need... important thing is making sure content is updated to truncated content before resetting truncatedHeight - use nextTick
                setTimeout(() => {
                    this.displayContent = this.getTruncatedContent();
                    this.expanded = false;
                    this.$nextTick(() => {
                        this.truncatedHeight = this.getContentHeight();  // do this in case window size / DOM changed
                    })
                }, delayTime);
            } else {
                // when expanding, update content first, then set height
                this.displayContent = this.content;
                this.expanded = true;  // do this here so everything renders correctly (including any CSS bindings) before getting/setting maxHeight
                this.$nextTick(() => {
                    this.parentHeight = this.getContentHeight();
                });
            }
        }
    }
}
</script>

<style lang="scss">

.text-expandable-parent {
    position: relative;
    overflow: hidden;
    //transition: 0.5s max-height;  // moved to template so I can use style binding
}

</style>